<template>
    <v-card>
        <v-card-title class="text-h5 bg-grey-lighten-2" primary-title>
            Edit {{ thingSchema.displayName }} {{ title }}
            <v-spacer />
            <span v-if="hasCondition">
                Condition: {{ thing.condition.number }} {{ thing.condition.name }}
            </span>
        </v-card-title>
        <v-card-text>
            <v-form @submit.prevent="save" class="pt-8" style="display: flex; flex-wrap: wrap">
                <div
                    class="mx-8"
                    style="width: 320px"
                    v-for="(formField, index) in formFields"
                    :key="formField.name"
                >
                    <thing-form-field
                        v-model="formFields[index]"
                        :thing-schema="thingSchema"
                        :experiment="experiment"
                        :protocol="protocol"
                    />
                </div>
            </v-form>
            <div v-if="dateTimeConfig" style="padding: 32px">
                <v-text-field
                    :type="dateTimeConfig.dateFieldType"
                    v-model="collectionDateTime"
                    :label="dateTimeConfig.collectionDateTimeLabel"
                    :style="
                        dateTimeConfig.dateFieldType == 'date' ? 'width: 320px' : 'width: 420px'
                    "
                >
                    <template v-slot:append> ({{ this.experiment.siteTimeZone }}) </template>
                </v-text-field>
            </div>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancel"> Cancel </v-btn>
            <v-btn color="primary" :disabled="!isFormValid" @click="save"> Save </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { defineComponent } from "vue";

import ThingFormField from "../common/ThingFormField.vue";
import {
    getFormFieldConfigForThingSchemaAttribute,
    getOneTimeAttributesList,
    getThingDisplay,
    validateFormFields,
} from "@/lib/thingUtils.ts";
import {
    findMeasurementForAttributeName,
    getEditabilityForMeasurement,
} from "@/lib/measurementsUtils.js";
import _ from "lodash";
import { mapGetters } from "vuex";
import { nowForExperiment } from "@/lib/dateUtils.js";
import { coerceValue } from "@/lib/coercer.js";

export default defineComponent({
    components: { ThingFormField },

    props: {
        thing: {
            type: Object,
            required: true,
        },
        measurements: {
            type: Array,
            required: true,
        },
        thingSchema: {
            type: Object,
            required: true,
        },
        dataCollectionTask: {
            type: Object,
            required: true,
        },
        taskSchedule: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: false,
        },
        readoutGroupAttributes: {
            type: Array,
            required: false,
        },
        dateTimeConfig: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            formFields: [],
            collectionDateTime: null,
        };
    },

    watch: {
        editMeasurementItem: {
            handler() {
                this.updateForm();
            },
            immediate: true,
        },
    },

    computed: {
        ...mapGetters({
            user: "getAuthUser",
        }),
        isFormValid() {
            return validateFormFields(this.formFields);
        },
        title() {
            return getThingDisplay(this.thing);
        },
        hasCondition() {
            return !_.isNil(this.thing.condition);
        },
    },
    mounted() {
        if (this.dateTimeConfig) {
            this.collectionDateTime = this.dateTimeConfig.editCollectionDateTimeISOString;
        }
    },
    methods: {
        updateForm() {
            const formFields = [];
            const thingAttributes = this.getAttributesList();
            for (const attribute of thingAttributes) {
                if (attribute.hidden) {
                    continue;
                }

                const measurement = findMeasurementForAttributeName(
                    attribute.name,
                    this.measurements
                );

                const value = measurement ? measurement.value : null;

                const formField = getFormFieldConfigForThingSchemaAttribute(
                    attribute,
                    value,
                    this.protocol
                );

                this.updateFormFieldForMeasurement(formField, measurement);
                formFields.push(formField);
            }

            this.formFields = formFields;
        },
        getAttributesList() {
            if (this.readoutGroupAttributes) {
                return this.readoutGroupAttributes;
            } else {
                return getOneTimeAttributesList(this.thingSchema);
            }
        },
        updateFormFieldForMeasurement(formField, measurement) {
            const editability = getEditabilityForMeasurement({
                measurement,
                dataCollectionTask: this.dataCollectionTask,
                experiment: this.experiment,
                taskSchedule: this.taskSchedule,
            });
            if (editability.editable) {
                formField.editable = true;
            } else {
                formField.editable = false;
                formField.disabledMessage = editability.disabledMessage;
            }
        },
        cancel() {
            this.closeDialog();
        },
        save() {
            if (this.isFormValid) {
                const measurements = this.buildMeasurementsFromFormFieldValues();
                this.$emit("save", measurements, this.collectionDateTime);
            }
        },
        buildMeasurementsFromFormFieldValues() {
            const now = nowForExperiment(this.experiment).toISO();
            const updatedMeasurements = [];
            for (const formField of this.formFields) {
                if (formField.disabled) {
                    // explicitly ignoring form fields that are disabled, even though they
                    // shouldn't have changed.
                    continue;
                }

                const measurement = this.mapFormFieldToMeasurement(formField, now);
                if (measurement) {
                    updatedMeasurements.push(measurement);
                }
            }

            return updatedMeasurements;
        },
        mapFormFieldToMeasurement(formField, now) {
            if (!this.isValidSettableMeasurementValue(formField)) {
                return null;
            }

            const rawValue = formField.value;
            const value = _.isNil(rawValue) ? null : coerceValue(formField.attribute, rawValue);

            return {
                attributeName: formField.attribute.name,
                collectionDateTime: now,
                timestamp: now,
                value,
            };
        },
        /**
         * Determines if the form field value is a valid value for a "settable" measurement.
         */
        isValidSettableMeasurementValue(formField) {
            const name = formField.name;
            const value = formField.value;
            console.log(name, value);

            if (!formField.editable) {
                // If the field isn't editable, then don't create a measurement for it.
                // Note that this reflects not being able to edit _summary_ measurement values
                // that came from a different task.
                // Note: to allow editing of values from _other_ tasks, this form would have to
                //  provide measurements in user measurement sets associated to the tasks
                //  associated with the summary measurement task.
                return false;
            }

            return true;
        },
        closeDialog() {
            this.$emit("closed");
        },
    },
});
</script>
